import { Ejendomsbeliggenhed } from "./EBRTypes";
import { DatafordelerGQL } from "./DatafordelerGQLCollector";

export class ebrCollector {

   /**
    * Fetches EBR data using GraphQL and transforms it to match the legacy GeoJSON FeatureCollection format
    * @deprecated The GraphQL response structure is different, but this maintains backward compatibility
    */
   static async EBRadresseForBFE(BFEnr: string): Promise<Ejendomsbeliggenhed> {
      // Use GraphQL instead of REST API
      const gqlResult = await DatafordelerGQL.EBR.EjendomsbeliggenhedForBFE(BFEnr);

      // Transform GraphQL response to legacy GeoJSON FeatureCollection format
      return transformGQLToGeoJSON(gqlResult);
   }

}

/**
 * Transforms GraphQL EBR response to legacy GeoJSON FeatureCollection format
 */
function transformGQLToGeoJSON(gqlResult: { EBR_Ejendomsbeliggenhed: { nodes: any[] } }): Ejendomsbeliggenhed {
   const nodes = gqlResult?.EBR_Ejendomsbeliggenhed?.nodes || [];

   const features = nodes.map((node) => {
      const husnummer = node.ejendomsbeliggenhedHarSomBeliggenhedsadresseHusnummer;

      // Transform husnummer to match legacy format
      const husnummerTransformed = husnummer ? transformHusnummer(husnummer) : undefined;
      const husnummerArray = husnummerTransformed ? [husnummerTransformed] : undefined;

      return {
         type: "Feature" as const,
         properties: {
            objectid: node.id_lokalId || "",
            id_namespace: node.id_namespace || "",
            id_lokalId: node.id_lokalId || "",
            bestemtFastEjendomBFENr: node.bestemtFastEjendomBFENr || "",
            Ejendomstype: "SamletFastEjendom" as const,
            adresseManueltAngivet: node.adresseManueltAngivet || false,
            ESDHReferenceAdresse: node.ESDHReferenceAdresse || null,
            ESDHReferenceKommune: node.ESDHReferenceKommune || null,
            kommuneManueltAngivet: node.kommuneManueltAngivet || false,
            kommuneinddelingKommunekode: node.kommuneinddelingKommunekode || "",
            betegnelse: node.betegnelse || null,
            adresse: [],
            husnummer: husnummerArray,
            status: node.status || "gældende",
            forretningshaendelse: node.forretningshaendelse || "ejendomsforandring",
            forretningsomraade: node.forretningsomraade || "52.20.05",
            forretningsproces: node.forretningsproces || "automatiskBeregnet",
            virkningFra: node.virkningFra || "",
            virkningTil: node.virkningTil || null,
            virkningsaktoer: node.virkningsaktoer || "",
            registreringFra: node.registreringFra || "",
            registreringTil: node.registreringTil || null,
            registreringsaktoer: node.registreringsaktoer || "",
         }
      };
   });

   return {
      type: "FeatureCollection",
      features
   };
}

/**
 * Transforms GraphQL husnummer structure to legacy DAR Husnummer format
 */
function transformHusnummer(gqlHusnummer: any) {
   if (!gqlHusnummer) return undefined;

   const postnummer = gqlHusnummer.husnummerHoererTilIPostnummer;
   const navngivenVej = gqlHusnummer.husnummerHoererTilNavngivenVej;
   const adgangspunkt = gqlHusnummer.HusnummerHarAdgangspunkt;
   const vejpunkt = gqlHusnummer.HusnummerHarVejpunkt;
   const afstemningsomraade = gqlHusnummer.husnummerLiggerIAfstemningsomraade;
   const kommuneinddeling = gqlHusnummer.husnummerLiggerIKommuneinddeling;
   const sogneinddeling = gqlHusnummer.husnummerLiggerISogneInddeling;
   const supplerendeBynavn = gqlHusnummer.husnummerHoererTilSupplerendeBynavn;

   return {
      datafordelerOpdateringstid: gqlHusnummer.datafordelerOpdateringstid || "",
      adgangsadressebetegnelse: gqlHusnummer.adgangsadressebetegnelse || "",
      adgangTilBygning: gqlHusnummer.adgangTilBygning || "",
      afstemningsområde: afstemningsomraade ? {
         id_lokalid: afstemningsomraade.afstemningsomraadenummer || "",
         afstemningsområdenummer: afstemningsomraade.afstemningsomraadenummer || "",
         navn: afstemningsomraade.navn || ""
      } : undefined,
      forretningshændelse: gqlHusnummer.forretningshaendelse || "",
      forretningsområde: gqlHusnummer.forretningsomraade || "",
      forretningsproces: gqlHusnummer.forretningsproces || "",
      geoDanmarkBygning: gqlHusnummer.geoDanmarkBygning || "",
      husnummerretning: gqlHusnummer.husnummerretning?.wkt || "",
      husnummertekst: gqlHusnummer.husnummertekst || "",
      id_lokalId: gqlHusnummer.id_lokalId || "",
      id_namespace: gqlHusnummer.id_namespace || "",
      jordstykke: gqlHusnummer.jordstykke || "",
      kommuneinddeling: kommuneinddeling ? {
         id_lokalid: kommuneinddeling.kommunekode || "",
         kommunekode: kommuneinddeling.kommunekode || "",
         navn: kommuneinddeling.navn || ""
      } : undefined,
      menighedsrådsafstemningsområde: undefined, // Not available in GraphQL query
      registreringFra: gqlHusnummer.registreringFra || "",
      registreringsaktør: gqlHusnummer.registreringsaktoer || "",
      sogneinddeling: sogneinddeling ? {
         id_lokalid: sogneinddeling.sognekode || "",
         sognekode: sogneinddeling.sognekode || "",
         navn: sogneinddeling.navn || ""
      } : undefined,
      status: gqlHusnummer.status || "",
      vejmidte: gqlHusnummer.vejmidte || "",
      virkningFra: gqlHusnummer.virkningFra || "",
      virkningsaktør: gqlHusnummer.virkningsaktoer || "",
      navngivenVej: navngivenVej ? {
         datafordelerOpdateringstid: navngivenVej.datafordelerOpdateringstid || "",
         administreresAfKommune: navngivenVej.administreresAfKommune || "",
         forretningshændelse: navngivenVej.forretningshaendelse || "",
         forretningsområde: navngivenVej.forretningsomraade || "",
         forretningsproces: navngivenVej.forretningsproces || "",
         id_lokalId: navngivenVej.id_lokalId || "",
         id_namespace: navngivenVej.id_namespace || "",
         registreringFra: navngivenVej.registreringFra || "",
         registreringsaktør: navngivenVej.registreringsaktoer || "",
         status: navngivenVej.status || "",
         udtaltVejnavn: navngivenVej.udtaltVejnavn || "",
         vejadresseringsnavn: navngivenVej.vejadresseringsnavn || "",
         vejnavn: navngivenVej.vejnavn || "",
         vejnavnebeliggenhed_oprindelse_kilde: navngivenVej.vejnavnebeliggenhed_oprindelse_kilde || "",
         vejnavnebeliggenhed_oprindelse_nøagtighedsklasse: navngivenVej.vejnavnebeliggenhed_oprindelse_noejagtighedsklasse || "",
         vejnavnebeliggenhed_oprindelse_registrering: navngivenVej.vejnavnebeliggenhed_oprindelse_registrering || "",
         vejnavnebeliggenhed_oprindelse_tekniskStandard: navngivenVej.vejnavnebeliggenhed_oprindelse_tekniskStandard || "",
         vejnavnebeliggenhed_vejnavnelinje: navngivenVej.vejnavnebeliggenhed_vejnavnelinje?.wkt || "",
         virkningFra: navngivenVej.virkningFra || "",
         virkningsaktør: navngivenVej.virkningsaktoer || "",
         navngivenVejKommunedelList: [] // Not in GraphQL query
      } : undefined,
      adgangspunkt: adgangspunkt ? {
         datafordelerOpdateringstid: adgangspunkt.datafordelerOpdateringstid || "",
         oprindelse_kilde: adgangspunkt.oprindelse_kilde || "",
         oprindelse_nøagtighedsklasse: adgangspunkt.oprindelse_noejagtighedsklasse || "",
         oprindelse_registrering: adgangspunkt.oprindelse_registrering || "",
         oprindelse_tekniskStandard: adgangspunkt.oprindelse_tekniskStandard || "",
         position: adgangspunkt.position?.wkt || ""
      } : undefined,
      vejpunkt: vejpunkt ? {
         datafordelerOpdateringstid: vejpunkt.datafordelerOpdateringstid || "",
         oprindelse_kilde: vejpunkt.oprindelse_kilde || "",
         oprindelse_nøagtighedsklasse: vejpunkt.oprindelse_noejagtighedsklasse || "",
         oprindelse_registrering: vejpunkt.oprindelse_registrering || "",
         oprindelse_tekniskStandard: vejpunkt.oprindelse_tekniskStandard || "",
         position: vejpunkt.position?.wkt || ""
      } : undefined,
      postnummer: postnummer ? {
         datafordelerOpdateringstid: postnummer.datafordelerOpdateringstid || "",
         forretningshændelse: postnummer.forretningshaendelse || "",
         forretningsområde: postnummer.forretningsomraade || "",
         forretningsproces: postnummer.forretningsproces || "",
         id_lokalId: postnummer.id_lokalId || "",
         id_namespace: postnummer.id_namespace || "",
         navn: postnummer.navn || "",
         postnr: postnummer.postnr || "",
         postnummerinddeling: postnummer.postnummerinddeling || "",
         registreringFra: postnummer.registreringFra || "",
         registreringsaktør: postnummer.registreringsaktoer || "",
         status: postnummer.status || "",
         virkningFra: postnummer.virkningFra || "",
         virkningsaktør: postnummer.virkningsaktoer || ""
      } : undefined,
      supplerendeBynavn: supplerendeBynavn ? {
         datafordelerOpdateringstid: supplerendeBynavn.datafordelerOpdateringstid || "",
         forretningshændelse: supplerendeBynavn.forretningshaendelse || "",
         forretningsområde: supplerendeBynavn.forretningsomraade || "",
         forretningsproces: supplerendeBynavn.forretningsproces || "",
         id_lokalId: supplerendeBynavn.id_lokalId || "",
         id_namespace: supplerendeBynavn.id_namespace || "",
         navn: supplerendeBynavn.navn || "",
         registreringFra: supplerendeBynavn.registreringFra || "",
         registreringsaktør: supplerendeBynavn.registreringsaktoer || "",
         status: supplerendeBynavn.status || "",
         supplerendeBynavn: supplerendeBynavn.supplerendeBynavn || "",
         virkningFra: supplerendeBynavn.virkningFra || "",
         virkningsaktør: supplerendeBynavn.virkningsaktoer || ""
      } : undefined
   };
}